import { useEffect, useReducer, useRef, useState } from 'react';
import { Button, Card, FieldError, Input, Label, Modal, TextArea, TextField } from '@heroui/react';

import ExportJsonLd from '../../common/ExportJsonLd';

import TimeAgo from '../../common/TimeAgo';
import TagsField from '../../common/TagsField';
import Notice from '../../common/Notice';
import FundingFields, { fundingPayloadOf } from '../../common/FundingFields';
import FundingSummary, { hasFunding } from '../../common/FundingSummary';
import RelatedResources from '../../common/RelatedResources';
import ResourceJudgements from '../judgements/ResourceJudgements';
import CollectionActivity from '../activity/CollectionActivity';
import ViewTab from '../../common/ViewTab';
import UserBadge from '../../common/UserBadge';
import StatusChip, { TagChip } from '../../common/StatusChip';
import { initialEditState, resourceEditReducer } from './resourceEdit';
import userService from '../../../services/auth.service';
import { kindOf } from '../../../services/resourceKind';

// Α2: **παραγόμενο αντίγραφο** της μίας εφεδρικής εικόνας του `design/`,
// όπως το λογότυπο (D8). Το πρόσθετο WordPress παίρνει το ίδιο αρχείο.
import resourceFallback from '../../../assets/img/resource-fallback.jpg';
import { detachWarning, messageOf, P1_5_TEXT, JUDGEMENTS, RESOURCE_COUNTS, RESOURCE_KIND, RESOURCE_HISTORY } from '../../../i18n/el';

// Κάρτα ψηφιακού πόρου (§4.3.3). Η επεξεργασία μέσα στην κάρτα καλύπτει τον
// χειροκίνητο εμπλουτισμό μεταδεδομένων της ΛΑ-10 και τις ετικέτες της ΛΑ-11.

/**
 * Σ2γ: **το είδος του πόρου**, ως ετικέτα.
 *
 * ⚠ Ο κανόνας ζει σε **ένα** σημείο (`services/resourceKind.js`) και **δεν**
 * αποθηκεύεται σε πεδίο: πεδίο θα έπρεπε να γεμίζεται από τρεις πελάτες, δηλαδή
 * τρεις διατυπώσεις, και θα **πάγωνε** — πόρος καταχωρημένος σήμερα δεν θα
 * ωφελούνταν από αυριανή βελτίωση του κανόνα.
 */
function ResourceKindChip({ url }) {
    const kind = kindOf(url);

    return (
        <StatusChip icon={RESOURCE_KIND.icons[kind]} tone="neutral" appearance="outline" className="self-start">
            {RESOURCE_KIND.labels[kind]}
        </StatusChip>
    );
}

/**
 * Α5: **το είδος πάνω στη φωτογραφία, ως εικονίδιο.**
 *
 * Στην κάρτα το λεκτικό chip καταλάμβανε ολόκληρη γραμμή για μία λέξη, και σε
 * πλέγμα τριών στηλών εκείνη η γραμμή είναι ακριβή. Το εικονίδιο λέει το ίδιο σε
 * μηδενικό ύψος. **Μέσα** στον πόρο μένουν και τα δύο —εικονίδιο και λεκτικό—
 * γιατί εκεί δεν υπάρχει πίεση χώρου και η λέξη είναι σαφέστερη.
 *
 * ⚠ **ΗΤΑΝ `<button>`, ΚΑΙ ΗΤΑΝ ΛΑΘΟΣ — ΤΟ ΕΙΠΕ Η ΚΟΝΣΟΛΑ.**
 *
 * Το επιχείρημα που το έκανε κουμπί ήταν σωστό ως **ανάγκη** και λάθος ως
 * **λύση**: το `title` ενός `<span>` εμφανίζεται μόνο με δείκτη ποντικιού, οπότε
 * όποιος πλοηγείται με `Tab` δεν το συναντούσε ποτέ. Η λύση όμως τοποθέτησε
 * κουμπί **μέσα σε κουμπί** — ολόκληρη η κάρτα είναι το κουμπί που ανοίγει τον
 * πόρο — και αυτό είναι **άκυρο HTML**: ο περιηγητής δεν εγγυάται τίποτα για τη
 * συμπεριφορά, και το React το ανέφερε ως σφάλμα ενυδάτωσης.
 *
 * **Η ανάγκη λύνεται χωρίς δεύτερο χειριστήριο.** Η ένδειξη γίνεται `<span>` με
 * `title` για τον δείκτη, και το λεκτικό μπαίνει **ως κρυφό κείμενο** μέσα στην
 * κάρτα: ο αναγνώστης οθόνης το εκφωνεί ως μέρος της περιγραφής της κάρτας —
 * όπου ανήκει, γιατί ο τύπος **είναι** ιδιότητα του πόρου και όχι ξεχωριστό
 * χειριστήριο.
 *
 * *Δεν υπάρχει πλέον τίποτα να πατηθεί, άρα ούτε διάδοση κλικ να σταματήσει.*
 *
 * **Ο χρήστης πληκτρολογίου που βλέπει** παίρνει το εικονίδιο, όπως πριν· εκείνος
 * που δεν βλέπει παίρνει τη λέξη. Η μόνη περίπτωση που χάνει κάτι είναι ο χρήστης
 * **αφής** που βλέπει και δεν αναγνωρίζει το εικονίδιο — και γι' αυτόν το
 * λεκτικό υπάρχει **ολόκληρο** στον ανοιχτό πόρο, δίπλα στον τίτλο.
 */
function ResourceKindBadge({ url, size = 'md' }) {
    const kind = kindOf(url);
    const label = RESOURCE_KIND.labels[kind];

    return (
        <span
            title={label}
            className={[
                'absolute right-3 bottom-3 flex items-center justify-center rounded-full bg-surface text-purple-800 shadow-sm',
                size === 'lg' ? 'h-9 w-9' : 'h-8 w-8'
            ].join(' ')}
        >
            <i className={RESOURCE_KIND.icons[kind]} aria-hidden="true" />
            <span className="sr-only">{label}</span>
        </span>
    );
}

/**
 * Σ2ε: τα τέσσερα πλήθη, **πριν** ανοίξει ο πόρος.
 *
 * ⚠ **ΤΑ ΣΧΟΛΙΑ ΚΑΙ ΟΙ ΑΞΙΟΛΟΓΗΣΕΙΣ ΕΙΝΑΙ ΤΗΣ ΤΡΕΧΟΥΣΑΣ ΣΥΛΛΟΓΗΣ (D13).** Ο
 * εξυπηρετητής τα υπολογίζει ήδη **ανά συλλογή** και τα στέλνει με κάθε πόρο:
 * μηδέν επιπλέον κόστος, και καμία διαρροή — ο κοινός πόρος δεν αποκαλύπτει
 * πόσες κρίσεις έχει **αλλού**. Η εκφώνηση το λέει ρητά, γιατί ο αριθμός μόνος
 * του θα διαβαζόταν ως καθολικός.
 *
 * **Το μηδέν δεν αποδίδεται.** Τέσσερα μηδενικά σε κάθε νέα κάρτα είναι θόρυβος
 * που κρύβει τα πραγματικά πλήθη των υπολοίπων.
 */
function ResourceCounts({ resource }) {
    const entries = [
        { key: 'tags', icon: 'fa-solid fa-tag', value: resource.tags?.length ?? 0 },
        { key: 'comments', icon: 'fa-regular fa-comment', value: resource.commentCount ?? 0 },
        { key: 'ratings', icon: 'fa-solid fa-star-half-stroke', value: resource.ratingCount ?? 0 },
        { key: 'related', icon: 'fa-solid fa-diagram-project', value: resource.relatedResources?.length ?? 0 }
    ].filter((entry) => entry.value > 0);

    if (entries.length === 0) return null;

    return (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
            {entries.map((entry) => (
                <span key={entry.key} className="flex items-center gap-1.5" aria-label={RESOURCE_COUNTS[entry.key](entry.value)}>
                    <i className={entry.icon} aria-hidden="true" />
                    <span aria-hidden="true">{entry.value}</span>
                </span>
            ))}
        </span>
    );
}

/*
 * ⚠ **ΕΔΩ ΖΟΥΣΕ Ο ΚΑΤΑΛΟΓΟΣ ΤΩΝ ΕΙΚΟΝΙΔΙΩΝ ΠΡΟΕΛΕΥΣΗΣ, ΚΑΙ ΑΦΑΙΡΕΘΗΚΕ ΟΛΟΚΛΗΡΟΣ.**
 *
 * Ήταν 29 ζεύγη «τμήμα διεύθυνσης → λογότυπο» και μία συνάρτηση αναζήτησης. Με την
 * αφαίρεση της δεύτερης σφραγίδας από τη φωτογραφία έπαψε να τον καλεί κανείς.
 *
 * **Δεν έμεινε «για κάθε ενδεχόμενο».** Κατάλογος που δεν διαβάζεται από πουθενά
 * είναι κώδικας που συντηρείται χωρίς να ελέγχεται: κανείς δεν θα παρατηρούσε αν
 * ένα λογότυπο μετονομαζόταν στη γραμματοσειρά εικονιδίων, γιατί κανείς δεν τον
 * αποδίδει. Το ιστορικό του αποθετηρίου τον κρατά, αν ξαναχρειαστεί.
 */

/**
 * Σύνδεσμοι κοινοποίησης, στη θέση του `react-share`.
 *
 * Η βιβλιοθήκη πρόσθετε τέσσερα συστατικά και μια peer dependency έως React 17
 * για να παράγει τέσσερις διευθύνσεις. Οι διευθύνσεις είναι δημόσιες και σταθερές,
 * οπότε γράφονται εδώ. Το `noopener` δεν είναι προαιρετικό: χωρίς αυτό η σελίδα
 * που ανοίγει αποκτά πρόσβαση στο `window.opener`.
 */
function shareLinks(resource) {
    const url = encodeURIComponent(resource.url);
    const title = encodeURIComponent(resource.title);

    return [
        { icon: 'fa-brands fa-facebook-f', label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${url}` },
        { icon: 'fa-brands fa-x-twitter', label: 'X', href: `https://twitter.com/intent/tweet?url=${url}&text=${title}` },
        { icon: 'fa-brands fa-linkedin-in', label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
        { icon: 'fa-solid fa-envelope', label: 'Ηλεκτρονικό ταχυδρομείο', href: `mailto:?subject=${title}&body=${url}` }
    ];
}

/**
 * @param {boolean} isOpen  Το άνοιγμα **δεν είναι πλέον τοπική κατάσταση**: το
 *   ορίζει η παράμετρος `?resource=` της διεύθυνσης, την οποία διαβάζει ο
 *   γονέας. Ήταν `#<id>` και εσωτερικό `useState`, που δούλευε μόνο για πόρο
 *   **ήδη φορτωμένο**: με τη σελιδοποίηση στον διακομιστή, σύνδεσμος προς πόρο
 *   της τρίτης σελίδας δεν άνοιγε τίποτα, γιατί το άγκιστρο δεν φτάνει ποτέ
 *   στον εξυπηρετητή για να ζητηθεί ο πόρος.
 * @param {(open: boolean) => void} onOpenChange Ο γονέας γράφει τη διεύθυνση.
 */
export default function ResourceItem({
    resource,
    collectionId,
    canEdit,
    onChanged,
    onReplace,
    isOpen = false,
    onOpenChange = () => {},
    isPinnedByLink = false,
    showCard = true,
    initialPane = 'resource',
    // ΛΑ-12: οι πόροι της φορτωμένης σελίδας. Δίνουν **ονόματα** στα
    // αναγνωριστικά των συσχετίσεων και γεμίζουν τον επιλογέα — χωρίς αυτούς, η
    // λίστα θα έκανε ένα αίτημα ανά συσχετισμένο πόρο για κείμενο που η σελίδα
    // έχει ήδη στη μνήμη της.
    siblings = [],
    // D49: οι ετικέτες που χρησιμοποιούνται ήδη **σε αυτή τη συλλογή**. Έρχονται
    // από τη σελίδα, που τις έχει ήδη υπολογίσει για την κάρτα «Ετικέτες»: καμία
    // δεύτερη πηγή και **καμία νέα διαδρομή**.
    knownTags = [],
    // P1-5β: οι τιμές που απορρίφθηκαν στην προσάρτηση, προωθημένες από τη σελίδα.
    // **Πρόχειρο, όχι αποθήκευση**: γεμίζουν τη φόρμα και περιμένουν τον άνθρωπο.
    forwardedDraft = null,
    onForwardConsumed = () => {}
}) {
    const [error, setError] = useState('');
    const [isSaving, setSaving] = useState(false);
    // Απόφαση D13: το βήμα επιβεβαίωσης πριν από την αποσύνδεση.
    const [isConfirmOpen, setConfirmOpen] = useState(false);
    // Ποια όψη φαίνεται. **Τέσσερις τιμές, μία κατάσταση, δύο μπάρες καρτελών**:
    //
    // - Σε **στενή** οθόνη υπάρχει μία στήλη και φαίνονται και οι τέσσερις καρτέλες.
    // - Σε **πλατιά** ο πόρος είναι πάντοτε ορατός αριστερά, οπότε πάνω από τη
    //   δεξιά στήλη φαίνονται μόνο οι τρεις τελευταίες.
    //
    // Η εναλλακτική —εξωτερική εναλλαγή «πόρος / κρίσεις» και δεύτερη μέσα της—
    // θα έδινε **εμφωλευμένες καρτέλες**: δύο μπάρες, η μία κάτω από την άλλη, με
    // την εσωτερική να αλλάζει νόημα ανάλογα με την εξωτερική. Μία κατάσταση με
    // τέσσερις τιμές λέει το ίδιο πράγμα χωρίς ιεραρχία.
    //
    // **Η τέταρτη είναι το ιστορικό (D46)**, και μπήκε δίπλα στις κρίσεις και όχι
    // δίπλα στον πόρο: απαντά «τι έγινε σε αυτόν», που είναι ερώτημα της ομάδας,
    // ενώ η αριστερή στήλη απαντά «τι είναι αυτός».
    // ⚠ **Η αρχική όψη έρχεται από τον ΚΑΛΟΥΝΤΑ, και μόνο ως αρχική.**
    //
    // Ο διάλογος ανοίγει από τρεις σημεία με **διαφορετική πρόθεση**: από την
    // κάρτα του πόρου («δείξε μου τον πόρο»), από τη λίστα «οι πιο σχολιασμένοι»
    // («δείξε μου τη συζήτηση») και από τη λίστα «οι πιο αξιολογημένοι» («δείξε
    // μου τις εκτιμήσεις»). Χωρίς αυτό, και τα τρία προσγειώνονταν στην ίδια
    // όψη — που είναι σωστή για το πρώτο και τυχαία για τα άλλα δύο.
    //
    // **Δεν μπαίνει στη διεύθυνση**, και είναι σκόπιμο: θα ήταν **τρίτη**
    // παράμετρος δίπλα στο `tab` και στο `resource`, για κατάσταση που δεν
    // αξίζει κοινοποίηση. Ο κοινοποιημένος σύνδεσμος δεν έχει κάρτα προέλευσης,
    // οπότε δεν έχει και πρόθεση να μεταφέρει: προσγειώνεται στην προεπιλογή.
    const [pane, setPane] = useState(initialPane);
    // Σε πλατιά οθόνη η δεξιά στήλη δεν μπορεί να είναι «ο πόρος»: όταν το `pane`
    // δείχνει εκεί, η δεξιά στήλη πέφτει στα **σχόλια**. Πρώτα η συζήτηση και μετά
    // οι εκτιμήσεις: το σχόλιο είναι αυτό που διαβάζεται, η εκτίμηση αυτό που
    // καταγράφεται — και η οθόνη ανοίγει για να διαβάσει κανείς.
    const judgementPane = pane === 'resource' ? 'comments' : pane;

    /**
     * Ο κόμβος που άνοιξε το modal, για να επιστρέψει εκεί η εστίαση.
     *
     * Δεν είναι λεπτομέρεια προσβασιμότητας: modal που κλείνει αφήνοντας την
     * εστίαση στο `<body>` υποχρεώνει τον χρήστη πληκτρολογίου να διασχίσει
     * ξανά ολόκληρη τη σελίδα για να φτάσει εκεί που ήταν. Με σαράντα κάρτες
     * στη σελίδα, αυτό είναι σαράντα `Tab`.
     */
    const triggerRef = useRef(null);
    const wasOpen = useRef(false);

    /**
     * **Σκέλος γ: η εστίαση επιστρέφει στο στοιχείο που άνοιξε το modal.**
     *
     * Ζει σε effect πάνω στο `isOpen` και όχι μέσα στον χειριστή κλεισίματος,
     * γιατί οι δρόμοι προς το κλείσιμο είναι **δύο** και μόνο ο ένας περνά από
     * τον χειριστή: το «Χ», το Esc και το backdrop καλούν `onOpenChange`, ενώ το
     * **κουμπί επιστροφής** αλλάζει τη διεύθυνση και φτάνει εδώ ως νέο `isOpen`
     * χωρίς να ειδοποιήσει κανέναν. Με τη λογική στον χειριστή, ο χρήστης
     * πληκτρολογίου που πατούσε «πίσω» έμενε με την εστίαση στο `<body>`.
     *
     * Η εστίαση μπαίνει σε **χρονιστή και όχι σε `requestAnimationFrame`**, και
     * αυτό μετρήθηκε: η παγίδα εστίασης του HeroUI, καθώς αποπροσαρτάται,
     * επαναφέρει την εστίαση σε ό,τι είχε καταγράψει πριν ανοίξει, οπότε
     * χρειάζεται να έρθουμε **μετά** από αυτήν. Το `requestAnimationFrame`
     * φαινόταν η φυσική επιλογή και είναι λάθος εδώ: σε καρτέλα που δεν είναι
     * ορατή ο περιηγητής **δεν συνθέτει καρέ και η επανάκληση δεν εκτελείται
     * ποτέ**. Η επαναφορά εστίασης θα δούλευε στην οθόνη και θα σιωπούσε σε
     * κάθε αυτοματοποιημένο έλεγχο — δηλαδή θα ήταν λειτουργία που καμία δοκιμή
     * δεν μπορεί να επιβεβαιώσει.
     */
    useEffect(() => {
        if (wasOpen.current && !isOpen) {
            // **Ο διάλογος επιβεβαίωσης κλείνει εδώ, ρητά.** Η προηγούμενη μορφή
            // στηριζόταν αποκλειστικά στη συνθήκη απόδοσης `{isOpen && isConfirmOpen}`,
            // δηλαδή έκρυβε τον διάλογο χωρίς να μηδενίζει την κατάστασή του: το
            // `isConfirmOpen` παρέμενε αληθές και η επόμενη είσοδος στον ίδιο πόρο
            // άνοιγε κατευθείαν στην επιβεβαίωση διαγραφής, χωρίς να το ζητήσει κανείς.
            //
            // Η αιτιολόγηση που είχε γραφτεί —ότι το συστατικό ξαναπροσαρτάται στην
            // επαναφόρτωση και η κατάσταση μηδενίζεται μόνη της— είναι **λάθος**: η
            // `fetchAll` του γονέα δεν μηδενίζει ποτέ τη συλλογή ή τους πόρους, οπότε
            // η λίστα δεν αντικαθίσταται από σκελετό, και τα κλειδιά των καρτών είναι
            // σταθερά. Το React **επαναχρησιμοποιεί** τα στιγμιότυπα και η τοπική
            // κατάσταση επιβιώνει ολόκληρη.
            setConfirmOpen(false);

            const timer = setTimeout(() => triggerRef.current?.focus(), 0);
            wasOpen.current = isOpen;
            return () => clearTimeout(timer);
        }

        wasOpen.current = isOpen;
        return undefined;
    }, [isOpen]);

    // Ο κύκλος ζωής της επεξεργασίας ζει σε καθαρό reducer, στο `resourceEdit.js`,
    // και δοκιμάζεται στο `resourceEdit.test.js`. Ο λόγος είναι συγκεκριμένος: η
    // ακύρωση αποτυγχάνει **σιωπηλά** — η οθόνη δείχνει τις αρχικές τιμές ακόμη και
    // όταν η κατάσταση κρατά τις ακυρωμένες, οπότε η οπτική επιθεώρηση δεν το πιάνει.
    const [{ isEditing, draft }, dispatch] = useReducer(resourceEditReducer, resource, initialEditState);

    // Όταν ο γονέας ξαναφορτώνει τη συλλογή, ο πόρος έρχεται με νέες τιμές. Το
    // πρόχειρο ακολουθεί, εκτός αν ο χρήστης βρίσκεται μέσα στην επεξεργασία.
    useEffect(() => { dispatch({ type: 'sync', resource }); }, [resource]);

    // ⚠ **Καταναλώνεται ΜΙΑ φορά.** Χωρίς την ειδοποίηση προς τον γονέα, το
    // πρόχειρο θα ξαναγέμιζε τη φόρμα κάθε φορά που ο χρήστης ανοίγει τον ίδιο
    // πόρο — με τιμές που ίσως έχει ήδη απορρίψει συνειδητά.
    useEffect(() => {
        if (!forwardedDraft) return;
        dispatch({ type: 'forward', resource, values: forwardedDraft });
        onForwardConsumed();
    }, [forwardedDraft]);

    // Ο ακροατής `hashchange` έφυγε μαζί με το `#<id>`: η παράμετρος της
    // διεύθυνσης παρακολουθείται από τον δρομολογητή, οπότε η αλλαγή φτάνει εδώ
    // ως νέο `isOpen` — και δουλεύει και όταν η πηγή είναι το κουμπί επιστροφής.

    const image = resource.image?.includes('://') ? resource.image : resourceFallback;

    function startEditing() {
        setError('');
        dispatch({ type: 'start', resource });
    }

    /**
     * Σ2α: η επικύρωση του τίτλου, **δίπλα στο πεδίο**.
     *
     * Ο εξυπηρετητής απορρίπτει ούτως ή άλλως με 422· ο έλεγχος εδώ γλιτώνει τον
     * κύκλο δικτύου και —το σημαντικότερο— δείχνει το μήνυμα **εκεί όπου
     * πληκτρολογεί ο χρήστης**, αντί για κοινό σημείο του διαλόγου που κυλά.
     */
    const titleProblem = isEditing && draft.title.trim().length < 2
        ? 'Ο τίτλος χρειάζεται τουλάχιστον δύο χαρακτήρες.'
        : '';

    function cancelEditing() {
        setError('');
        dispatch({ type: 'cancel', resource });
    }

    function save() {
        // Ο τίτλος πρώτος: είναι το **μόνο** υποχρεωτικό πεδίο, και η άρνησή του
        // φαίνεται ήδη δίπλα στο πεδίο — εδώ απλώς δεν φεύγει το αίτημα.
        if (titleProblem) return;

        // **Το ποσό διαβάζεται ΠΡΙΝ φύγει το αίτημα.** Αν δεν διαβάζεται, η
        // αποθήκευση σταματά εδώ: ο χρήστης βλέπει τι φταίει και τα υπόλοιπα
        // πεδία μένουν ως έχουν. Σιωπηλή αποστολή κενού θα έδινε μήνυμα
        // επιτυχίας για ποσό που χάθηκε.
        const payload = fundingPayloadOf(draft.funding);
        if (payload.error) {
            setError(payload.error);
            return;
        }

        setSaving(true);
        setError('');

        // Το `collectionId` είναι ήδη prop του συστατικού: η ενημέρωση φεύγει από τη
        // διαδρομή που **ξέρει σε ποια συλλογή** έγινε, ώστε να εκπεμφθεί το
        // `resource.updated` με τη συλλογή της ενέργειας και όχι με μια από τις
        // συλλογές του κοινού πόρου (Μ1).
        const draftToSend = { ...draft, funding: payload.funding };

        userService.updateResource(collectionId, resource.id, draftToSend).then(
            () => {
                dispatch({ type: 'saved', resource: { ...resource, ...draftToSend } });
                setSaving(false);
                onChanged();
            },
            (apiError) => {
                setError(messageOf(apiError));
                setSaving(false);
            }
        );
    }

    // Απόφαση D9: η αφαίρεση αφορά **αυτή** τη συλλογή και μόνο. Ο πόρος είναι
    // κοινός, οπότε χωρίς το πλαίσιο της συλλογής η ενέργεια θα τον έσβηνε και από
    // κάθε άλλη συλλογή του χρήστη — και μαζί την επιμέλεια άλλης ομάδας.
    function remove() {
        setError('');
        userService.deleteResource(collectionId, resource.id).then(
            () => onChanged(),
            (apiError) => setError(messageOf(apiError))
        );
    }

    /**
     * Απόφαση D13: η αποσύνδεση διαγράφει **και τις κρίσεις** αυτής της συλλογής.
     *
     * Η ΜΛΑ-03 απαιτεί προειδοποίηση πριν από μη αναστρέψιμη ενέργεια, και εδώ το
     * κόστος δεν είναι ορατό: ο χρήστης νομίζει ότι αφαιρεί έναν σύνδεσμο, ενώ
     * σβήνει σχόλια και αξιολογήσεις της ομάδας του — δικά του και άλλων. Το πλήθος
     * έρχεται με την ίδια απόκριση που φόρτωσε την κάρτα, χωρίς δεύτερο αίτημα.
     */
    function askToRemove() {
        setError('');
        setConfirmOpen(true);
    }

    /**
     * Απόφαση D10, σκέλος β: η διαδρομή διόρθωσης λάθος διεύθυνσης.
     *
     * **Δεν αποσυνδέει τίποτα εδώ.** Ανοίγει τη φόρμα καταχώρησης προσυμπληρωμένη,
     * και η αποσύνδεση του παλιού γίνεται από τον γονέα **μόνο μετά** από επιτυχή
     * δημιουργία του νέου. Αν το κάναμε αντίστροφα, η εγκατάλειψη της φόρμας θα
     * άφηνε τον χρήστη χωρίς τον πόρο — και αν δεν ανήκε σε άλλη συλλογή, το
     * έγγραφο θα είχε διαγραφεί μαζί με σχόλια και αξιολογήσεις. Θα ήταν η ίδια
     * απώλεια που διορθώνει η υπο-διαδρομή της συλλογής.
     */
    function replaceAddress() {
        onOpenChange(false);
        cancelEditing();
        onReplace(resource);
    }

    const set = (name) => (value) => dispatch({ type: 'change', name, value });

    function handleOpenChange(open) {
        onOpenChange(open);
        // Το κλείσιμο ακυρώνει την επεξεργασία, όπως το κουμπί ακύρωσης. Αλλιώς η
        // επόμενη είσοδος στο modal θα ξεκινούσε με μισοσυμπληρωμένο πρόχειρο.
        // Η επαναφορά της εστίασης γίνεται στο effect παραπάνω, ώστε να καλύπτει
        // και το κουμπί επιστροφής, που δεν περνά από εδώ.
        if (!open) cancelEditing();
    }

    return (
        <>
            {/* **Ο διάλογος είναι ορθογώνιος στις καρτέλες της σελίδας (Β1δ).**
                Με το `showCard` ψευδές αποδίδεται **μόνο** αυτός: είναι η
                περίπτωση του μόνιμου συνδέσμου `?resource=` όταν η ενεργή
                καρτέλα δεν είναι οι Πόροι, οπότε η λίστα —άρα και η κάρτα που
                κρατά τον διάλογο— δεν αποδίδεται καθόλου.

                Ποιος από τους δύο πιθανούς οικοδεσπότες αναλαμβάνει, το κρίνει
                **μία** συνάρτηση στο `browseState.js`, και όχι δύο συνθήκες
                απόδοσης που θα μπορούσαν να αποκλίνουν: και οι δύο τρόποι
                αποτυχίας —κανένας διάλογος, δύο διάλογοι— είναι σιωπηλοί. */}
            {showCard && (
            <Card className="card--interactive mb-4 break-inside-avoid gap-0 p-0">
                {/* Απόφαση Α8α: ο πόρος του συνδέσμου **μένει** στη λίστα, αλλά δηλώνει
                    γιατί βρίσκεται εκεί. Η εναλλακτική —να μην αποδίδεται καθόλου— είναι
                    χειρότερη: ο χρήστης κλείνει τον διάλογο και δεν βρίσκει πουθενά αυτό
                    που μόλις κοίταζε, σε μια λίστα που φαινομενικά τα έχει όλα. Η
                    φαινομενική παραβίαση της ταξινόμησης είναι μικρότερο κακό από την
                    εξαφάνιση, και η σήμανση αίρει την ένσταση: η θέση παύει να είναι
                    ανεξήγητη. */}
                {isPinnedByLink && (
                    <p className="flex items-center gap-1.5 rounded-t-3xl bg-accent-soft px-4 py-2 text-sm text-accent">
                        <i className="fa-solid fa-thumbtack" aria-hidden="true" />
                        {P1_5_TEXT.pinnedByLink}
                    </p>
                )}
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={() => onOpenChange(true)}
                    className="flex flex-col rounded-3xl text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                >
                    {/* **Αναλογία 1.91:1, από τη σταθερά και όχι από αριθμό εδώ.**
                        Είναι η αναλογία που ορίζει το Open Graph για το `og:image`,
                        δηλαδή ακριβώς αυτή με την οποία έρχονται οι εικόνες από την
                        άντληση: με σταθερό ύψος παραμορφώνονταν και οι κάρτες είχαν
                        ανόμοιο ύψος. Η τιμή ζει στο `design/tokens.json` και τη
                        διαβάζουν **και τα δύο** σημεία που δείχνουν εικόνα πόρου. */}
                    <div className="relative aspect-[var(--ds-og-aspect)] w-full shrink-0 overflow-hidden rounded-t-3xl bg-purple-100">
                        <img src={image} alt="" className="h-full w-full object-cover" />
                        {/* ⚠ **ΜΙΑ ΕΝΔΕΙΞΗ ΣΤΗΝ ΕΙΚΟΝΑ, ΟΧΙ ΔΥΟ.** Η ένδειξη
                            **προέλευσης** —ο συνδετήρας και τα λογότυπα των γνωστών
                            ιστότοπων— αφαιρέθηκε. Το επιχείρημα που την κρατούσε
                            ήταν ότι απαντά σε **άλλο ερώτημα**, και στέκει· δεν
                            στέκει όμως ότι το ερώτημα αξίζει **δεύτερη σφραγίδα
                            πάνω στη φωτογραφία**: η διεύθυνση φαίνεται ολόκληρη
                            μέσα στον πόρο, και για τους περισσότερους πόρους το
                            εικονίδιο ήταν ο **γενικός συνδετήρας**, δηλαδή σύμβολο
                            που δεν έλεγε τίποτα.

                            Μένει ο **τύπος**, κάτω δεξιά, στο κύριο χρώμα. */}
                        <ResourceKindBadge url={resource.url} />
                    </div>

                    <div className="flex flex-col gap-2 p-4">
                        {/* ⚠ **Α5: ΤΟ ΛΕΚΤΙΚΟ CHIP ΕΦΥΓΕ ΑΠΟ ΕΔΩ.** Το επιχείρημα
                            που το είχε βάλει —ο τύπος αλλάζει τον τρόπο που
                            διαβάζεις τον τίτλο, «Συνέντευξη Παπαδάκη» σημαίνει
                            άλλο ως ήχος και άλλο ως σελίδα— **στέκει ακόμη**, και
                            γι' αυτό ο τύπος δεν αφαιρέθηκε αλλά **μετακόμισε**:
                            είναι το εικονίδιο πάνω αριστερά στη φωτογραφία, που το
                            μάτι το συναντά **πριν** από τον τίτλο και κοστίζει
                            μηδέν ύψος. Σε πλέγμα τριών στηλών, μια ολόκληρη γραμμή
                            για μία λέξη ήταν η ακριβότερη γραμμή της κάρτας. */}
                        <Card.Title className="line-clamp-2">{resource.title}</Card.Title>
                        {resource.description && (
                            <p className="line-clamp-3 text-sm text-muted">{resource.description}</p>
                        )}
                        <FundingSummary funding={resource.funding} compact />

                        {/* Σ2ε: τα πλήθη **πριν** ανοίξει ο πόρος. */}
                        <ResourceCounts resource={resource} />

                        <span className="mt-1 flex items-center gap-1.5 border-t border-border pt-3 text-sm text-muted">
                            <i className="fa-solid fa-clock" aria-hidden="true" />
                            <TimeAgo date={resource.updatedAt} />
                        </span>
                    </div>
                </button>
            </Card>
            )}

            {/* **Αποδίδεται μόνο όταν είναι ανοιχτό**, και αυτό είναι διόρθωση
                ελαττώματος και όχι στιλιστική επιλογή.

                Το `Modal.Root` του HeroUI **δεν κλείνει όταν το `isOpen` γίνεται
                ψευδές από τον γονέα**: κλείνει μόνο όταν το κλείσιμο το ξεκινά το
                ίδιο, μέσω `Modal.CloseTrigger`, Esc ή backdrop. Όσο η κατάσταση
                ήταν τοπική αυτό δεν φαινόταν ποτέ, γιατί το prop άλλαζε πάντα ως
                **συνέπεια** του δικού του `onOpenChange` — το prop συμφωνούσε με
                μια ενέργεια που είχε ήδη γίνει.

                Με τον μόνιμο σύνδεσμο η κατάσταση έρχεται από τη διεύθυνση, οπότε
                υπάρχει δρόμος που ο ίδιος ο διάλογος δεν γνωρίζει: το **κουμπί
                επιστροφής**. Επαληθεύτηκε στον περιηγητή ότι μετά το «πίσω» η
                διεύθυνση καθαρίζει, το React ξαναποδίδει με `isOpen === false`,
                και ο διάλογος **παραμένει ανοιχτός** — δηλαδή το σκέλος γ δεν
                ίσχυε. Η αποπροσάρτηση το κλείνει με βεβαιότητα, ανεξάρτητα από
                το ποιος ξεκίνησε το κλείσιμο. */}
            {isOpen && (
            <Modal.Root isOpen onOpenChange={handleOpenChange}>
                <Modal.Backdrop>
                    {/* **Πλάτος 75vw, στο `Dialog` και όχι στο `Container`.** Το
                        `size="lg"` δίνει `max-w-lg` — 512 πίξελ — και το δίνει στο
                        **dialog**. Το ίδιο όριο γραμμένο στο `container` δεν είχε τι να
                        περιορίσει, γιατί εκεί το πλάτος είναι ήδη `w-fit`: μετρήθηκε
                        στον περιηγητή και το παράθυρο έμενε στα 512, με το 75vw να
                        ισχύει για ένα στοιχείο που δεν φαινόταν. Κάτω από τα 640
                        μένει το προεπιλεγμένο: εκεί το 75vw θα άφηνε περιθώρια που δεν
                        χωρούν κείμενο. */}
                    <Modal.Container size="lg">
                        <Modal.Dialog className="w-full sm:max-w-[75vw]">
                            <Modal.Header>
                                <div className="flex min-w-0 flex-1 flex-col gap-2">
                                    {/* ⚠ **Σ2α: Ο ΤΙΤΛΟΣ ΔΕΝ ΕΧΕΙ ΠΕΔΙΟ ΜΕ ΕΤΙΚΕΤΑ.**
                                        Επεξεργάζεται **στη θέση του**, με το ίδιο
                                        μέγεθος και το ίδιο βάρος: ο χρήστης δεν
                                        χρειάζεται να βρει σε ποιο από πέντε πεδία
                                        αντιστοιχεί ο τίτλος που βλέπει. Ένα πεδίο
                                        κάτω από τη φόρμα, με ετικέτα «Τίτλος», ζητά
                                        από τον αναγνώστη να κάνει την αντιστοίχιση
                                        μόνος του κάθε φορά.

                                        Η επικύρωση **μένει** και το μήνυμα κάθεται
                                        **από κάτω**, στη θέση του πεδίου — όχι σε
                                        κοινό σημείο του διαλόγου, που κυλά. */}
                                    {isEditing ? (
                                        <div className="flex flex-col gap-1">
                                            <TextField
                                                value={draft.title}
                                                onChange={set('title')}
                                                aria-label="Τίτλος"
                                                isInvalid={Boolean(titleProblem)}
                                            >
                                                <Input className="text-xl font-semibold" />
                                            </TextField>
                                            {titleProblem && <FieldError>{titleProblem}</FieldError>}
                                        </div>
                                    ) : (
                                        // ⚠ **Α5 + Α7: Ο ΤΥΠΟΣ ΔΙΠΛΑ ΣΤΟΝ ΤΙΤΛΟ, ΚΑΙ Ο
                                        //     ΤΙΤΛΟΣ ΔΕΝ ΚΟΒΕΤΑΙ ΠΛΕΟΝ.**
                                        //
                                        // Ήταν **πάνω** από τον τίτλο, σε δική του γραμμή:
                                        // ο τύπος διαβάζεται μαζί με το όνομα, όχι πριν από
                                        // αυτό, και σε στενή οθόνη η χαμένη γραμμή ήταν
                                        // ακριβώς εκείνη που έλειπε από τον τίτλο.
                                        //
                                        // Το `line-clamp-2` **φεύγει**. Ο διάλογος δεν είναι
                                        // πλέγμα καρτών: εδώ δεν υπάρχει γειτονικό στοιχείο
                                        // να ευθυγραμμιστεί, οπότε το κόψιμο δεν αγόραζε
                                        // τίποτα και **κόστιζε το όνομα** — σε στενή οθόνη
                                        // ένας τίτλος τριών γραμμών τελείωνε σε «…», δηλαδή
                                        // η οθόνη του πόρου δεν έλεγε ποιος είναι ο πόρος.
                                        // Το `break-words` είναι για τη μία μακριά λέξη
                                        // (διεύθυνση, ακρωνύμιο) που αλλιώς σπρώχνει
                                        // οριζόντια ολόκληρο τον διάλογο.
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                            <Modal.Heading className="min-w-0 break-words">{resource.title}</Modal.Heading>
                                            <ResourceKindChip url={resource.url} />
                                        </div>
                                    )}

                                    {/* ⚠ **Σ2β: ΟΙ ΕΤΙΚΕΤΕΣ ΑΝΗΚΟΥΝ ΣΤΟΝ ΤΙΤΛΟ.**
                                        Ήταν χωριστή ενότητα μέσα στο σώμα, μετά την
                                        περιγραφή — δηλαδή ο αναγνώστης έπρεπε να
                                        διαβάσει ολόκληρο το κείμενο για να μάθει πώς
                                        έχει ταξινομηθεί ο πόρος. Είναι **ταυτότητα**,
                                        όχι περιεχόμενο: κάθονται δίπλα στο όνομα. */}
                                    {!isEditing && resource.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {resource.tags.map((tag) => (
                                                <TagChip key={tag} appearance="outline">{tag}</TagChip>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <Modal.CloseTrigger />
                            </Modal.Header>

                            {/* **Β10α: δύο στήλες, με δική τους κύλιση η καθεμία.**
                                Αριστερά ο πόρος, δεξιά οι κρίσεις. Σε μία στήλη, το
                                σχόλιο απαιτούσε κύλιση πέρα από ολόκληρη την εικόνα και
                                την περιγραφή, οπότε ο χρήστης έγραφε χωρίς να βλέπει τι
                                σχολιάζει.

                                Η υπό συνθήκη διάταξη ισχύει **μόνο εκτός επεξεργασίας**:
                                η φόρμα είναι μία ροή πεδίων και δεν κερδίζει τίποτα από
                                δεύτερη στήλη — θα κέρδιζε μόνο σύγχυση, με δύο κουμπιά
                                υποβολής σε διαφορετικά ύψη.

                                Σε στενή οθόνη οι στήλες γίνονται **εναλλαγή**, με το ίδιο
                                `ViewTab` που χρησιμοποιεί ο κατάλογος συλλογών για τις
                                «Ενεργές / Αρχειοθετημένες». */}
                            {/* **Η κύλιση δεν γράφεται εδώ, και αυτό ήταν το λάθος.**
                                Το HeroUI έχει ήδη μηχανισμό: με `scroll="inside"`, που είναι
                                η προεπιλογή, ο διάλογος παίρνει `max-h-full` και το σώμα
                                `overflow-y-auto` ως **άμεσο παιδί flex**. Ένα δικό μου
                                `max-h-[70vh]` προστέθηκε πάνω του και δεν διόρθωνε τίποτε:
                                εκεί που η αλυσίδα ήταν ολόκληρη, ήταν περιττό· εκεί που είχε
                                σπάσει — στα τρία παράθυρα με `<form>` γύρω από τα Modal.* —
                                δεν την επισκεύαζε. Μετρήθηκε και στις δύο διατάξεις. */}
                            <Modal.Body className="flex flex-col gap-4">
                                {!isEditing && (
                                    <div className="flex flex-wrap gap-2 lg:hidden">
                                        <ViewTab
                                            icon="fa-solid fa-file-lines"
                                            isActive={pane === 'resource'}
                                            onPress={() => setPane('resource')}
                                        >
                                            {JUDGEMENTS.tabResource}
                                        </ViewTab>
                                        <ViewTab
                                            icon="fa-solid fa-comments"
                                            isActive={pane === 'comments'}
                                            onPress={() => setPane('comments')}
                                        >
                                            {JUDGEMENTS.tabComments}
                                        </ViewTab>
                                        <ViewTab
                                            icon="fa-solid fa-star-half-stroke"
                                            isActive={pane === 'ratings'}
                                            onPress={() => setPane('ratings')}
                                        >
                                            {JUDGEMENTS.tabRatings}
                                        </ViewTab>
                                        <ViewTab
                                            icon="fa-solid fa-clock-rotate-left"
                                            isActive={pane === 'history'}
                                            onPress={() => setPane('history')}
                                        >
                                            {RESOURCE_HISTORY.tab}
                                        </ViewTab>
                                    </div>
                                )}

                                <div className={isEditing
                                    ? 'flex flex-col gap-4'
                                    : 'grid gap-6 lg:grid-cols-2'}
                                >
                                <div className={[
                                    'flex flex-col gap-4',
                                    isEditing ? '' : 'lg:max-h-[60vh] lg:overflow-y-auto lg:pe-2',
                                    !isEditing && pane !== 'resource' ? 'hidden lg:flex' : ''
                                ].join(' ')}>
                                <div className="relative aspect-[var(--ds-og-aspect)] w-full shrink-0 overflow-hidden rounded-2xl bg-purple-100">
                                    <img src={image} alt="" className="h-full w-full object-cover" />
                                    {/* Η ίδια μία ένδειξη με την κάρτα, στο **μεγάλο**
                                        μέγεθος. Η θέση δεν επιτρέπεται να αλλάζει
                                        ανάμεσα στην κάρτα και στην ανοιχτή όψη του
                                        **ίδιου** πράγματος: ο χρήστης μαθαίνει πού
                                        κοιτά, μία φορά. */}
                                    <ResourceKindBadge url={resource.url} size="lg" />
                                </div>

                                {/* Κάτω από την εικόνα: **αριστερά ποιος**, δεξιά **πότε
                                    και πού**. Η ταυτότητα του συντάκτη είναι η πληροφορία
                                    που διαβάζεται πρώτη και παίρνει τη θέση ανάγνωσης· ο
                                    χρόνος και ο σύνδεσμος είναι βοηθητικά και στοιχίζονται
                                    στο αντίθετο άκρο, ώστε να μη διεκδικούν την ίδια θέση. */}
                                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm text-muted">
                                    {resource.createdBy
                                        ? <UserBadge user={resource.createdBy} size="sm" />
                                        : <span />}

                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                    <span className="flex items-center gap-1.5">
                                        <i className="fa-solid fa-clock" aria-hidden="true" />
                                        <TimeAgo date={resource.updatedAt} />
                                    </span>
                                    <a
                                        href={resource.url}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        className="flex items-center gap-1.5 rounded-full font-medium text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                                    >
                                        <i className="fa-solid fa-link" aria-hidden="true" />
                                        Άνοιγμα του συνδέσμου
                                    </a>
                                    </div>
                                </div>

                                {error && <Notice status="danger" title="Η ενέργεια απέτυχε">{error}</Notice>}

                                {isEditing ? (
                                    <>
                                        {/* Απόφαση D10, σκέλος α: το πεδίο εμφανίζεται ΑΝΕΝΕΡΓΟ και όχι
                                            κρυμμένο. Κρυμμένο, ο χρήστης θα αναρωτιόταν αν ξέχασε κάτι ή
                                            αν η εφαρμογή έχασε τη διεύθυνση· ανενεργό, βλέπει τι δεν
                                            αλλάζει και γιατί. Η άρνηση επιβάλλεται ούτως ή άλλως στο API
                                            με 422 (§4.4): η φόρμα εξηγεί, δεν αποφασίζει. */}
                                        <TextField value={resource.url} isDisabled>
                                            <Label>Διεύθυνση</Label>
                                            <Input />
                                            <span className="text-sm text-muted">
                                                Η διεύθυνση είναι η ταυτότητα του πόρου και δεν αλλάζει:
                                                τα σχόλια και οι αξιολογήσεις κρέμονται από αυτήν.
                                            </span>
                                        </TextField>

                                        <TextField value={draft.description} onChange={set('description')}>
                                            <Label>Περιγραφή</Label>
                                            <TextArea rows={4} />
                                        </TextField>

                                        <TextField value={draft.image} onChange={set('image')}>
                                            <Label>Διεύθυνση εικόνας</Label>
                                            <Input placeholder="https://..." />
                                        </TextField>

                                        <TagsField
                                            value={draft.tags}
                                            onChange={set('tags')}
                                            suggestions={knownTags}
                                        />
                                        <FundingFields value={draft.funding} onChange={set('funding')} />
                                    </>
                                ) : (
                                    <>
                                        {/* ⚠ **Σ2δ: Η ΠΕΡΙΓΡΑΦΗ ΣΕ ΔΙΚΟ ΤΗΣ ΠΛΑΙΣΙΟ.**
                                            Ήταν ελεύθερο κείμενο ανάμεσα σε ετικέτες,
                                            σύνοψη χρηματοδότησης και συσχετίσεις —
                                            τέσσερα πράγματα στη σειρά, χωρίς τίποτα να
                                            λέει πού τελειώνει το ένα. Το πλαίσιο δεν
                                            είναι διακόσμηση: είναι το **όριο** που
                                            επιτρέπει στο μάτι να πηδήξει την περιγραφή
                                            όταν ψάχνει κάτι άλλο.

                                            Οι **ετικέτες έφυγαν** από εδώ και ανέβηκαν
                                            δίπλα στον τίτλο (Σ2β). */}
                                        {resource.description && (
                                            <p className="rounded-xl border border-border bg-surface p-4 whitespace-pre-line">
                                                {resource.description}
                                            </p>
                                        )}

                                        {/* ⚠ **ΤΟ ΔΙΑΧΩΡΙΣΤΙΚΟ ΑΠΟΔΙΔΟΤΑΝ ΚΑΙ ΟΤΑΝ ΔΕΝ
                                            ΕΙΧΕ ΤΙ ΝΑ ΧΩΡΙΣΕΙ.** Η σύνοψη
                                            χρηματοδότησης επιστρέφει `null` για πόρο
                                            χωρίς στοιχεία — σωστά — αλλά το
                                            **περίβλημά** της αποδιδόταν πάντοτε, οπότε
                                            η κάρτα έδειχνε μια γραμμή που δεν χώριζε
                                            τίποτα από τίποτα.

                                            Η συνθήκη **δεν ξαναγράφεται εδώ**: έρχεται
                                            από το `hasFunding`, την ίδια συνάρτηση που
                                            χρησιμοποιεί και η σύνοψη για να αποφασίσει
                                            αν θα αποδοθεί. Δύο διατυπώσεις του «έχει
                                            χρηματοδότηση;» θα απέκλιναν στο πρώτο νέο
                                            πεδίο, και η απόκλιση θα ήταν **ακριβώς
                                            αυτή**: γραμμή χωρίς περιεχόμενο, ή
                                            περιεχόμενο χωρίς γραμμή. */}
                                        {hasFunding(resource.funding) && (
                                            <div className="border-t border-border pt-4">
                                                <FundingSummary funding={resource.funding} />
                                            </div>
                                        )}

                                        {/* ΛΑ-12. Μέσα στην περιοχή **του πόρου**, κάτω από τις
                                            ετικέτες: η συσχέτιση είναι δεδομένο του πόρου και όχι
                                            κρίση, οπότε δεν ανήκει στη δεξιά στήλη. */}
                                        <RelatedResources
                                            resource={resource}
                                            collectionId={collectionId}
                                            resources={siblings}
                                            canEdit={canEdit}
                                            onChanged={onChanged}
                                        />

                                        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
                                            <span className="text-sm font-medium">Κοινοποίηση</span>
                                            {shareLinks(resource).map(({ icon, label, href }) => (
                                                <a
                                                    key={label}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                    aria-label={`Κοινοποίηση σε ${label}`}
                                                    className="flex h-11 w-11 items-center justify-center rounded-full text-muted hover:bg-accent-soft hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                                                >
                                                    <i className={icon} aria-hidden="true" />
                                                </a>
                                            ))}
                                        </div>
                                        {/* ══ ΣΗΜΕΙΟ ΠΡΟΣΑΡΤΗΣΗΣ P1-3 — σχόλια και αξιολογήσεις του πόρου ══
                                            Το P1-3 προσθέτει εδώ ΜΙΑ γραμμή, π.χ.
                                            <ResourceJudgements resourceId={resource.id}
                                            collectionId={collectionId} canWrite={canEdit} />.

                                            Εδώ και όχι στην κάρτα: η κάρτα είναι περίληψη σε δύο
                                            στήλες και το modal είναι η πλήρης όψη του πόρου.

                                            Εντός του κλάδου ανάγνωσης και ΟΧΙ επεξεργασίας: στην
                                            επεξεργασία η οθόνη είναι φόρμα με ανοιχτό πρόχειρο, και
                                            μια φόρμα σχολίου μέσα σε άλλη φόρμα δίνει δύο κουμπιά
                                            υποβολής που μοιάζουν ίδια (ΜΛΑ-03).

                                            **Το `collectionId` είναι υποχρεωτικό**, όχι προαιρετικό:
                                            με την D9 ο πόρος είναι κοινός και οι κρίσεις είναι ανά
                                            συλλογή. Σχόλιο χωρίς συλλογή δεν έχει πού να γραφτεί.
                                            Είναι ήδη prop του συστατικού.

                                            Διαθέσιμα εδώ: resource, collectionId, canEdit, onChanged.
                                            ══ ΤΕΛΟΣ P1-3 ══════════════════════════════════════════ */}
                                    </>
                                )}
                                </div>

                                {/* Η δεξιά στήλη: οι κρίσεις, με δική τους κύλιση. Δεν
                                    αποδίδεται καθόλου στην επεξεργασία — βλ. παραπάνω. */}
                                {!isEditing && (
                                    <div className={[
                                        'flex flex-col gap-4 lg:max-h-[60vh] lg:overflow-y-auto lg:pe-2',
                                        pane === 'resource' ? 'hidden lg:flex' : ''
                                    ].join(' ')}>
                                        {/* Η δεύτερη μπάρα, μόνο σε πλατιά οθόνη: εκεί ο
                                            πόρος φαίνεται ήδη αριστερά, οπότε η καρτέλα
                                            του θα ήταν επιλογή χωρίς αποτέλεσμα. */}
                                        <div className="hidden gap-2 lg:flex">
                                            <ViewTab
                                                icon="fa-solid fa-comments"
                                                isActive={judgementPane === 'comments'}
                                                onPress={() => setPane('comments')}
                                            >
                                                {JUDGEMENTS.tabComments}
                                            </ViewTab>
                                            <ViewTab
                                                icon="fa-solid fa-star-half-stroke"
                                                isActive={judgementPane === 'ratings'}
                                                onPress={() => setPane('ratings')}
                                            >
                                                {JUDGEMENTS.tabRatings}
                                            </ViewTab>
                                            <ViewTab
                                                icon="fa-solid fa-clock-rotate-left"
                                                isActive={judgementPane === 'history'}
                                                onPress={() => setPane('history')}
                                            >
                                                {RESOURCE_HISTORY.tab}
                                            </ViewTab>
                                        </div>

                                        {/* ══ D46 — ΤΟ ΙΣΤΟΡΙΚΟ ΤΟΥ ΠΟΡΟΥ ══════════════════
                                            Ίδιο πρότυπο με τον διάλογο του καθηκόντος
                                            (**D41**), και **ίδιο συστατικό**: το
                                            `CollectionActivity` δεχόταν ήδη `resource`,
                                            με σχόλιο που το ονόμαζε «ολόκληρη απαίτηση —
                                            ΛΑ-23». Κανένας δεν του το περνούσε. Δεν
                                            χρειάστηκε ούτε εξαγωγή, ούτε νέα διαδρομή.

                                            ⚠ **Ο φράχτης ορατότητας δεν γράφεται εδώ και
                                            δεν αντιγράφεται:** είναι ο `loadCollection` +
                                            `authorize` της **ίδιας** διαδρομής που φυλάει
                                            τη ροή της συλλογής και τις συσχετίσεις. Το
                                            `resource` δεν είναι πόρτα εισόδου — δένει το
                                            `objectId`, αφού ο έλεγχος έχει ήδη περάσει.

                                            **Το εύρος είναι Η ΣΥΛΛΟΓΗ, όχι ο πόρος.** Με
                                            τη **D9** ο πόρος είναι κοινός, οπότε ό,τι
                                            έγινε αλλού φτάνει εδώ **μόνο** ως
                                            `resource.updated_elsewhere` — γεγονός που
                                            **δεν ονομάζει** την άλλη συλλογή, γιατί το
                                            όνομά της είναι ύπαρξη που ο αναγνώστης δεν
                                            δικαιούται. Γι' αυτό και η κενή κατάσταση λέει
                                            «σε αυτή τη συλλογή».

                                            **Υπό συνθήκη απόδοση, όχι κρυμμένη με κλάση:**
                                            το ιστορικό κάνει **δικό του αίτημα**, και
                                            κρυφό συστατικό που ρωτά τον εξυπηρετητή είναι
                                            κόστος για κάτι που κανείς δεν κοιτά. Ο ίδιος
                                            λόγος με τη D41.
                                            ══════════════════════════════════════════════ */}
                                        {judgementPane === 'history' ? (
                                            <CollectionActivity
                                                collectionId={collectionId}
                                                resource={resource.id}
                                                title={RESOURCE_HISTORY.title}
                                                emptyMessage={RESOURCE_HISTORY.empty}
                                            />
                                        ) : (
                                            <ResourceJudgements
                                                resourceId={resource.id}
                                                collectionId={collectionId}
                                                canWrite={canEdit}
                                                pane={judgementPane}
                                            />
                                        )}
                                    </div>
                                )}
                                </div>
                            </Modal.Body>

                            {/* **Το υποσέλιδο αποδίδεται πάντοτε**, και δεν είναι
                                αλλαγή διάταξης: ήταν πίσω από το `canEdit`, οπότε
                                όποιος **διαβάζει** τον πόρο χωρίς να τον γράφει δεν
                                είχε κανένα κουμπί — ούτε την εξαγωγή, που είναι δική
                                του (ΛΑ-28). Οι δύο ενέργειες επιμέλειας μένουν πίσω
                                από το `canEdit`, όπως πριν. */}
                            {/* ⚠ **Α7: ΤΟ ΥΠΟΣΕΛΙΔΟ ΑΝΑΔΙΠΛΩΝΕΤΑΙ.** Τρία κουμπιά με
                                εικονίδιο και ελληνικό λεκτικό —«Εξαγωγή JSON-LD»,
                                «Διαγραφή», «Επεξεργασία»— δεν χωρούν σε μία γραμμή
                                κάτω από τα ~380 πίξελ, και χωρίς αναδίπλωση το
                                τελευταίο **έβγαινε έξω από τον διάλογο**: όχι
                                κομμένο, **απρόσιτο**.

                                Το `justify-end` κρατά τη στοίχιση της μίας γραμμής
                                όταν χωρούν, ώστε η αλλαγή να μη φαίνεται πουθενά
                                αλλού. Το `gap-2` αντικαθιστά το περιθώριο που το
                                HeroUI δίνει μόνο οριζόντια — χωρίς αυτό, οι δύο
                                γραμμές κολλάνε. */}
                            <Modal.Footer className="flex flex-wrap justify-end gap-2">
                                {/* ΛΑ-28: ο **ίδιος** πόρος, μέσα στο ρητό πλαίσιο της
                                    συλλογής (D9/D15) — η εξαγωγή ενός πόρου εκτός
                                    συλλογής δεν έχει νόημα, γιατί οι κρίσεις του
                                    ανήκουν στη σχέση και όχι στον πόρο. */}
                                {/* **Όχι κατά την επεξεργασία**, για δύο λόγους. Η
                                    εξαγωγή θα έβγαζε την **αποθηκευμένη** μορφή ενώ ο
                                    χρήστης κοιτά τις αλλαγές του, δηλαδή αρχείο που
                                    δεν αντιστοιχεί σε αυτό που βλέπει· και η
                                    «Λάθος διεύθυνση;» κρατά ήδη την αριστερή άκρη —
                                    δύο στοιχεία με την ίδια εντολή στοίχισης δίνουν
                                    το ένα από αυτά στη μέση του υποσέλιδου. */}
                                {!isEditing && (
                                    <ExportJsonLd
                                        path={`/api/collections/${collectionId}/resources/${resource.id}/export.jsonld`}
                                        fallbackName="resource.jsonld"
                                        size="sm"
                                        className="me-auto"
                                    />
                                )}
                                {canEdit && (
                                    <>
                                    {isEditing ? (
                                        <>
                                            {/* Απόφαση D10, σκέλος β: η διαδρομή διόρθωσης, από την ίδια
                                                οθόνη όπου ο χρήστης διαπιστώνει το λάθος. */}
                                            <Button variant="outline" onPress={replaceAddress} className="me-auto">
                                                <i className="fa-solid fa-link-slash" aria-hidden="true" />
                                                Λάθος διεύθυνση;
                                            </Button>
                                            <Button variant="outline" onPress={cancelEditing}>Ακύρωση</Button>
                                            <Button onPress={save} isPending={isSaving}>Αποθήκευση</Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button variant="danger" onPress={askToRemove}>
                                                <i className="fa-solid fa-trash" aria-hidden="true" />
                                                Διαγραφή
                                            </Button>
                                            <Button onPress={startEditing}>
                                                <i className="fa-solid fa-pen" aria-hidden="true" />
                                                Επεξεργασία
                                            </Button>
                                        </>
                                    )}
                                    </>
                                )}
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal.Root>
            )}

            {/* Απόφαση D13 και ΜΛΑ-03: η επιβεβαίωση δηλώνει το πλήθος των κρίσεων
                που θα διαγραφούν. Ήταν η μόνη μη αναστρέψιμη ενέργεια της οθόνης
                χωρίς κανένα βήμα επιβεβαίωσης — ένα κλικ έσβηνε και σχόλια άλλων.

                **Υπό συνθήκη, με ΔΥΟ σκέλη, κατά τον κανόνα του P1-5 παραπάνω.** Το
                `isConfirmOpen` κλείνει από τα δικά του κουμπιά, αλλά το `isOpen`
                κλείνει και από τη διεύθυνση — το κουμπί επιστροφής του περιηγητή.

                **ΔΙΟΡΘΩΘΗΚΕ 19/08, από πραγματικό χειροκίνητο έλεγχο.** Εδώ ήταν
                γραμμένο ότι η μετάλλαξη δοκιμάστηκε, δεν αναπαρήγαγε την αστοχία, και
                άρα η προστασία υπάρχει ως **παρενέργεια της επαναπροσάρτησης**. Η
                αιτιολόγηση ήταν λάθος και επαληθεύεται με ανάγνωση: η `fetchAll` του
                γονέα **δεν μηδενίζει ποτέ** τη συλλογή ή τους πόρους, οπότε η λίστα
                δεν αντικαθίσταται ποτέ από σκελετό μετά την πρώτη φόρτωση, και τα
                κλειδιά των καρτών είναι σταθερά. **Επαναπροσάρτηση δεν συμβαίνει.**

                Η συνθήκη μένει, αλλά η κατάσταση μηδενίζεται πλέον **ρητά** στο effect
                του `isOpen` παραπάνω. Η διαφορά δεν είναι στιλιστική: η συνθήκη μόνη
                της **κρύβει** τον διάλογο χωρίς να τον κλείνει, οπότε το
                `isConfirmOpen` επιβίωνε και η επόμενη είσοδος στον ίδιο πόρο άνοιγε
                κατευθείαν στην επιβεβαίωση διαγραφής. */}
            {isOpen && isConfirmOpen && (
            <Modal.Root isOpen onOpenChange={setConfirmOpen}>
                <Modal.Backdrop>
                    <Modal.Container>
                        <Modal.Dialog>
                            <Modal.Header>
                                {/* `Modal.Heading` και ΟΧΙ `Modal.Title`: το δεύτερο δεν
                                    υπάρχει στο HeroUI 3 και απέδιδε `undefined`, δηλαδή
                                    «Element type is invalid» και **λευκή σελίδα** μόλις
                                    πατούσε κανείς «Διαγραφή». Το build περνούσε κανονικά —
                                    η πρόσβαση σε ανύπαρκτη ιδιότητα αντικειμένου είναι
                                    έγκυρη έκφραση — και καμία δοκιμή δεν το έβλεπε, γιατί
                                    το `web/` δεν έχει jsdom. */}
                                <Modal.Heading>Αφαίρεση από τη συλλογή</Modal.Heading>
                            </Modal.Header>
                            <Modal.Body>
                                <p className="text-sm">
                                    {`Ο πόρος «${resource.title}» θα αφαιρεθεί από αυτή τη συλλογή.`}
                                </p>
                                {detachWarning(resource) && (
                                    <Notice status="warning" title={detachWarning(resource)} />
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="outline" onPress={() => setConfirmOpen(false)}>
                                    Ακύρωση
                                </Button>
                                <Button
                                    variant="danger"
                                    onPress={() => {
                                        setConfirmOpen(false);
                                        remove();
                                    }}
                                >
                                    Αφαίρεση
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal.Root>
            )}
        </>
    );
}
